import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import {
  evaluateAllEvalRunsMutation,
  getEvaluatorOptions,
  setEvaluatorVersionMutation,
  updateEvaluatorMutation,
} from '@/api/generated/@tanstack/react-query.gen';
import type { EvalAlignment, EvaluatorDetail } from '@/api/generated/types.gen';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { EvalShell, Panel } from '@/components/Evals/EvalShell';
import { pct } from '@/components/Evals/format';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/_auth/evals/evaluators/$evaluatorId')({
  component: EvaluatorPage,
});

const LINE_HOW = [
  'After a run, the judge gets the tutor’s base instructions, the lesson script, and the transcript. Every tutor line carries its id, the step directive the model had at that moment, and what the screen showed.',
  'It labels every tutor line. The run’s score is the share labeled with the first label (good).',
  '{examples}: up to “few-shot” of your labeled lines from other runs, half wrong and half right, with your “should have said”. This is the main lever for alignment.',
  'Alignment compares its labels with your Right/Wrong labels on the same lines. Edit the prompt, save (a new version), re-score, compare.',
];
const RUN_HOW = [
  'After a run, the judge reads the base instructions, the script, and the whole transcript, and scores the session 1–5 (shown as 0–100%).',
];

function EvaluatorPage() {
  const { evaluatorId } = Route.useParams();
  const query = useQuery(getEvaluatorOptions({ path: { evaluator_id: evaluatorId } }));
  const data = query.data;
  return (
    <EvalShell
      title={data?.evaluator.name ?? 'Evaluator'}
      subtitle={data?.evaluator.description}
      crumbs={[{ label: 'Evaluators', to: '/evals/evaluators' }]}
    >
      {!data ? <Skeleton className="h-96 rounded-3xl" /> : <EvaluatorBody key={`${data.evaluator.id}-${data.evaluator.version}`} data={data} />}
    </EvalShell>
  );
}

function EvaluatorBody({ data }: { data: EvaluatorDetail }) {
  const queryClient = useQueryClient();
  const ev = data.evaluator;
  const current = ev.current;
  const path = { path: { evaluator_id: ev.id } };
  const refresh = () => queryClient.invalidateQueries();
  const update = useMutation({ ...updateEvaluatorMutation(), onSuccess: () => (toast.success('Saved'), refresh()) });
  const setVersion = useMutation({ ...setEvaluatorVersionMutation(), onSuccess: () => (toast.success('Version switched'), refresh()) });
  const rescore = useMutation({
    ...evaluateAllEvalRunsMutation(),
    onSuccess: (r) => toast.success(`${r.updated} runs queued for scoring`),
  });
  const form = useForm({
    defaultValues: {
      model: current?.llm?.model ?? '',
      prompt: current?.llm?.prompt ?? '',
      labels: (current?.llm?.labels ?? ['good', 'bad']).join(', '),
      few_shot: current?.llm?.few_shot ?? 0,
      threshold: current?.threshold ?? '',
      note: '',
    },
  });

  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_380px]">
      <div className="space-y-4">
        <Panel
          title={ev.kind === 'llm' ? `Judge · v${ev.version}` : `Check · v${ev.version}`}
          aside={
            <label className="flex items-center gap-2 text-[12px] text-neutral-600">
              Active
              <Switch checked={ev.active} onCheckedChange={(active) => update.mutate({ ...path, body: { active, note: '' } })} />
            </label>
          }
        >
          <form
            className="space-y-3"
            onSubmit={form.handleSubmit((v) =>
              update.mutate({
                ...path,
                body:
                  ev.kind === 'llm'
                    ? {
                        llm: {
                          model: v.model,
                          prompt: v.prompt,
                          labels: v.labels.split(',').map((l) => l.trim()).filter(Boolean),
                          few_shot: Number(v.few_shot),
                          output: current?.llm?.output ?? 'score',
                        },
                        note: v.note,
                      }
                    : { threshold: v.threshold === '' ? null : Number(v.threshold), note: v.note },
              }),
            )}
          >
            {ev.kind === 'llm' ? (
              <>
                <div className="flex flex-wrap gap-2">
                  <label className="space-y-1">
                    <span className="text-[11px] uppercase tracking-wide text-neutral-500">Model</span>
                    <Input className="h-8 w-48 text-xs" {...form.register('model')} />
                  </label>
                  {ev.target === 'line' && (
                    <>
                      <label className="space-y-1">
                        <span className="text-[11px] uppercase tracking-wide text-neutral-500">Labels</span>
                        <Input className="h-8 w-32 text-xs" {...form.register('labels')} />
                      </label>
                      <label className="space-y-1">
                        <span className="text-[11px] uppercase tracking-wide text-neutral-500">Few-shot</span>
                        <Input type="number" min={0} max={30} className="h-8 w-20 text-xs" {...form.register('few_shot')} />
                      </label>
                    </>
                  )}
                </div>
                <Textarea className="min-h-96 font-mono text-[12px] leading-relaxed" {...form.register('prompt')} />
              </>
            ) : (
              <label className="block space-y-1">
                <span className="text-[11px] uppercase tracking-wide text-neutral-500">Threshold</span>
                <Input type="number" step="any" className="h-8 w-32 text-xs" {...form.register('threshold')} />
              </label>
            )}
            <div className="flex gap-2">
              <Input placeholder="What changed (saved as a new version)" className="h-9" {...form.register('note')} />
              <Button type="submit" disabled={update.isPending} className="btn-3d h-9 rounded-full px-5">
                Save version
              </Button>
            </div>
          </form>
        </Panel>

        {ev.target === 'line' && (
          <Panel title={`Where it disagrees with you · ${data.disagreements.length}`}>
            {!data.disagreements.length ? (
              <p className="text-sm text-neutral-400">
                No disagreements on the current version (or nothing to compare yet).
              </p>
            ) : (
              <div className="space-y-2">
                {data.disagreements.map((d) => (
                  <div key={`${d.room_id}-${d.target_id}`} className="rounded-2xl bg-neutral-50 px-3 py-2">
                    <p className="text-[13px] text-neutral-800">“{d.text}”</p>
                    <p className="mt-1 text-[12px]">
                      <span className={d.human === 'bad' ? 'text-rose-600' : 'text-emerald-600'}>You: {d.human}</span>
                      <span className="text-neutral-400"> · </span>
                      <span className={d.judge === 'bad' ? 'text-rose-600' : 'text-emerald-600'}>Judge: {d.judge}</span>
                      <span className="text-neutral-500"> · {d.explanation}</span>
                    </p>
                    {d.human_notes && <p className="text-[12px] text-neutral-500">Your note: {d.human_notes}</p>}
                    <Link
                      to="/reviews/$roomId"
                      params={{ roomId: d.room_id }}
                      className="text-[12px] font-medium text-neutral-700 hover:underline"
                    >
                      Open session
                    </Link>
                  </div>
                ))}
              </div>
            )}
          </Panel>
        )}
      </div>

      <div className="space-y-4">
        <Panel title="How it works">
          <ul className="list-disc space-y-1.5 pl-4 text-[12px] leading-relaxed text-neutral-600">
            {(ev.kind === 'code' ? [ev.description] : ev.target === 'line' ? LINE_HOW : RUN_HOW).map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </Panel>
        {ev.target === 'line' && (
          <Panel
            title="Alignment with your labels"
            aside={
              <Button variant="outline" className="h-7 rounded-full px-3 text-[12px]" onClick={() => rescore.mutate({})}>
                Re-score all runs
              </Button>
            }
          >
            <div className="space-y-3">
              {data.alignments.map((a) => (
                <AlignmentRow key={a.version} alignment={a} current={a.version === ev.version} />
              ))}
            </div>
          </Panel>
        )}
        <Panel title="Versions">
          <div className="space-y-1.5">
            {data.versions.map((v) => (
              <div key={v.version} className="flex items-start justify-between gap-2 text-[12px]">
                <div>
                  <span className="font-medium text-neutral-800">v{v.version}</span>
                  <span className="text-neutral-500"> · {v.note || 'no note'}</span>
                  <p className="text-[11px] text-neutral-400">{v.created_at ? new Date(v.created_at).toLocaleString() : ''}</p>
                </div>
                {v.version === ev.version ? (
                  <span className="rounded-full bg-[#AAE73D] px-2 py-0.5 text-[10px] font-semibold uppercase text-neutral-900">current</span>
                ) : (
                  <button
                    type="button"
                    className="text-[12px] font-medium text-neutral-700 hover:underline"
                    onClick={() => setVersion.mutate({ path: { evaluator_id: ev.id, version: v.version } })}
                  >
                    Use
                  </button>
                )}
              </div>
            ))}
          </div>
        </Panel>
      </div>
    </div>
  );
}

function AlignmentRow({ alignment, current }: { alignment: EvalAlignment; current: boolean }) {
  const [open, setOpen] = useState(current);
  const labels = ['good', 'bad'];
  return (
    <div className={cn('rounded-2xl px-3 py-2', current ? 'bg-neutral-50' : '')}>
      <button type="button" className="flex w-full items-center justify-between text-left" onClick={() => setOpen(!open)}>
        <span className="text-[13px] font-medium text-neutral-800">v{alignment.version}</span>
        <span className="text-[12px] text-neutral-600">
          {alignment.compared ? `${pct(alignment.agreement)} on ${alignment.compared} lines` : 'nothing to compare'}
        </span>
      </button>
      {open && (alignment.compared ?? 0) > 0 && (
        <div className="mt-2 space-y-2">
          <table className="w-full text-center text-[12px]">
            <thead>
              <tr className="text-[10px] uppercase tracking-wide text-neutral-400">
                <th className="text-left font-medium">you ↓ judge →</th>
                {labels.map((l) => (
                  <th key={l} className="font-medium">
                    {l}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {labels.map((h) => (
                <tr key={h}>
                  <td className="text-left text-neutral-600">{h}</td>
                  {labels.map((j) => (
                    <td
                      key={j}
                      className={cn('py-1 font-mono', h === j ? 'text-emerald-700' : 'text-rose-600')}
                    >
                      {alignment.matrix?.[h]?.[j] ?? 0}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-[11px] text-neutral-500">
            Catches {pct(alignment.bad_recall)} of the lines you marked wrong; {pct(alignment.bad_precision)} of what it flags you
            agree with.
          </p>
        </div>
      )}
    </div>
  );
}
